// AXIS ZERO - Start Screen
// Title screen with START and LEADERBOARD, no practice mode

import React, { useState, useEffect, useRef } from 'react';
import { Play, Volume2, VolumeX, Trophy, Loader2 } from 'lucide-react';
import { GAME_STATES, leaderboardAPI } from '../../game';

export const StartScreen = ({ 
  gameState, 
  highScore, 
  personalBestRank,
  onStart, 
  isMuted,
  onToggleSound,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [glowPhase, setGlowPhase] = useState(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState(false);
  
  const mainColor = '#00E5FF';
  const glowColor = 'rgba(0, 229, 255, 0.8)';
  const fetchedRef = useRef(false);

  useEffect(() => {
    setIsVisible(gameState === GAME_STATES.READY);
    if (gameState === GAME_STATES.READY) {
      fetchLeaderboard();
      setShowLeaderboard(false);
    }
  }, [gameState]);

  useEffect(() => {
    const interval = setInterval(() => {
      setGlowPhase(prev => (prev + 0.05) % (Math.PI * 2));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchLeaderboard();
    }
  }, []);

  const fetchLeaderboard = async () => {
    setLoadingLeaderboard(true);
    setLeaderboardError(false);
    try {
      const data = await leaderboardAPI.fetchTopScores(10);
      setLeaderboardData(data);
    } catch (e) {
      setLeaderboardError(true);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  if (!isVisible) return null;

  const glowIntensity = 0.5 + Math.sin(glowPhase) * 0.3;
  const showYourBest = highScore !== null && highScore !== undefined && highScore > 0;

  const formatHighScore = () => {
    if (highScore === null || highScore === undefined) return '\u2014';
    return `${highScore.toFixed(2)}s`;
  };

  return (
    <div 
      data-testid="start-screen"
      className="absolute inset-0 z-40 flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: '#000000' }}
    >
      {/* Sound Toggle */}
      <button
        data-testid="sound-toggle"
        onClick={onToggleSound}
        className="absolute top-4 right-4 p-3 transition-all z-10"
        style={{ color: isMuted ? '#333' : mainColor }}
      >
        {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
      </button>

      {!showLeaderboard ? (
        <div className="flex flex-col items-center w-full max-w-sm">
          {/* Title: AXIS ZERO */}
          <h1 
            data-testid="game-title"
            className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[0.15em] mb-8"
            style={{ 
              color: mainColor,
              textShadow: `0 0 ${20 * glowIntensity}px ${glowColor}, 0 0 ${40 * glowIntensity}px ${glowColor}`,
              fontFamily: '"Orbitron", sans-serif',
            }}
          >
            AXIS ZERO
          </h1>

          {/* Your Best */}
          {showYourBest && (
            <div 
              data-testid="your-best-section"
              className="flex items-center gap-2 mb-6"
              style={{ fontFamily: '"Rajdhani", sans-serif' }}
            >
              <Trophy size={14} style={{ color: mainColor, opacity: 0.6 }} />
              <span className="text-sm uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Best
              </span>
              <span 
                data-testid="start-high-score"
                className="text-lg font-bold tracking-wider"
                style={{ color: mainColor, textShadow: `0 0 8px ${glowColor}` }}
              >
                {formatHighScore()}
              </span>
              {personalBestRank && (
                <span className="text-sm" style={{ color: '#FF1744' }}>#{personalBestRank}</span>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col gap-3 w-full items-center mb-8">
            {/* START Button - Primary CTA */}
            <button
              data-testid="start-button"
              onClick={onStart}
              className="group w-full max-w-[240px] px-10 py-4 border-2 font-bold uppercase tracking-[0.3em] transition-all duration-200 flex items-center justify-center gap-3"
              style={{ 
                borderColor: mainColor,
                color: mainColor,
                backgroundColor: 'transparent',
                fontFamily: '"Orbitron", sans-serif',
                fontSize: '1.2rem',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = mainColor;
                e.currentTarget.style.color = '#000';
                e.currentTarget.style.boxShadow = `0 0 30px ${glowColor}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = mainColor;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Play size={20} fill="currentColor" />
              START
            </button>

            {/* LEADERBOARD Button - Secondary */}
            <button
              data-testid="leaderboard-button"
              onClick={() => setShowLeaderboard(true)}
              className="flex items-center gap-2 px-6 py-2 text-sm uppercase tracking-widest transition-all opacity-50 hover:opacity-100"
              style={{ 
                color: mainColor,
                fontFamily: '"Rajdhani", sans-serif',
              }}
            >
              <Trophy size={14} />
              LEADERBOARD
            </button>
          </div>

          {/* Instructions */}
          <div 
            className="text-center"
            style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: '"Rajdhani", sans-serif' }}
          >
            <div className="text-xs uppercase tracking-widest mb-1">Tap anywhere to jump</div>
            <div className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.2)' }}>
              Avoid obstacles &middot; Survive as long as you can
            </div>
          </div>
        </div>
      ) : (
        /* Leaderboard View */
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Trophy size={18} style={{ color: mainColor }} />
              <h2 
                className="text-lg font-bold uppercase tracking-wider"
                style={{ color: mainColor, fontFamily: '"Orbitron", sans-serif' }}
              >
                TOP 10
              </h2>
            </div>
            <button
              data-testid="leaderboard-close"
              onClick={() => setShowLeaderboard(false)}
              className="text-sm uppercase tracking-wider opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: mainColor, fontFamily: '"Rajdhani", sans-serif' }}
            >
              BACK
            </button>
          </div>

          {loadingLeaderboard ? (
            <div className="flex items-center justify-center py-8" style={{ color: '#666' }}>
              <Loader2 className="animate-spin mr-2" size={18} />
              Loading...
            </div>
          ) : leaderboardError ? (
            <div className="text-center py-8 text-sm" style={{ color: '#666' }}>
              Unable to load leaderboard
            </div>
          ) : leaderboardData.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: '#666' }}>
              No scores yet. Be the first!
            </div>
          ) : (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {leaderboardData.map((entry, index) => (
                <div 
                  key={`${entry.nickname}-${index}`}
                  className="flex items-center justify-between p-2.5 rounded"
                  style={{ 
                    backgroundColor: index < 3 ? 'rgba(0, 229, 255, 0.08)' : 'rgba(255,255,255,0.03)',
                    borderLeft: index < 3 ? `3px solid ${mainColor}` : 'none',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span 
                      className="font-bold w-7 text-xs"
                      style={{ 
                        color: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#666',
                        fontFamily: '"Orbitron", sans-serif',
                      }}
                    >
                      #{index + 1}
                    </span>
                    <span className="text-sm truncate max-w-[120px]" style={{ color: '#fff' }}>
                      {entry.nickname || 'PLAYER'}
                    </span>
                  </div>
                  <span 
                    className="font-bold text-sm"
                    style={{ color: mainColor, fontFamily: '"Orbitron", sans-serif' }}
                  >
                    {(entry.score / 100).toFixed(2)}s
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StartScreen;
